import React, { useEffect, useState } from 'react';
import { CiCircleMinus } from 'react-icons/ci';
import { FaCircleMinus } from 'react-icons/fa6';
import ImgSlider from '../components/ImgSlider';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import * as contApi from '../api/continue';
import * as pagesApi from '../api/pagesStore';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const HomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
    overflow: hidden;
`;

const Content = styled.div`
  flex: 1;
  margin-top: 54px; /* posune ImgSlider lehce dolů */
`;

function Home() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [contReady, setContReady] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null); // { popupId, title }

  // Live subscribe to continueSeries like Watchlist does, and enrich from popup so thumbnails reflect Admin changes
  useEffect(() => {
    if (!user) { setItems([]); setContReady(true); return; }
    let mounted = true;
    setContReady(false);
    const ref = doc(db, 'users', user.uid);
    const unsub = onSnapshot(ref, async (snap) => {
      try {
        const data = snap.exists() ? (snap.data() || {}) : {};
        const cs = data.continueSeries || {};
        const legacy = data.continue || {};
        const combined = [
          ...Object.values(cs || {}),
          ...Object.values(legacy || {}),
        ];
        combined.sort((a,b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0));
        // grab more than we need to allow deduplication after enrichment
        const base = combined.slice(0, 40);

        // Enrichment using latest popup and pages data to reflect new thumbnails/titles
        const byPopup = new Map();
        const playExists = new Map();
        let _popupsCache = null;
        const popupsFetchOnce = async () => {
          if (_popupsCache) return _popupsCache;
          try {
            const r = await fetch('/api/popups', { cache: 'no-store' });
            if (r.ok) {
              const j = await r.json();
              if (Array.isArray(j)) _popupsCache = j;
            }
          } catch {}
          return _popupsCache || [];
        };
        const pageExists = async (playId) => {
          if (!playId) return false;
          const key = String(playId);
          if (playExists.has(key)) return playExists.get(key);
          try {
            const pages = await pagesFetchOnce();
            const path = `/play/${key}`;
            const it = Array.isArray(pages) ? pages.find(p => p.path === path && !p.disabled) : null;
            const ok = !!it;
            playExists.set(key, ok);
            return ok;
          } catch { return false; }
        };
        let _pagesCache = null;
        const pagesFetchOnce = async () => {
          if (_pagesCache) return _pagesCache;
          try {
            const l = await pagesApi.getPages();
            if (Array.isArray(l)) _pagesCache = l;
          } catch {}
          return _pagesCache || [];
        };

        const enrichOne = async (it) => {
          try {
            const pid = (it.playId || (it.path||'').split('/').pop() || '').toString();
            // ensure popupId first from existing data or page content
            let ensuredPopupId = it.popupId || null;
            if (!ensuredPopupId && pid) {
              try {
                const pages = await pagesFetchOnce();
                const path = `/play/${pid}`;
                const rec = Array.isArray(pages) ? pages.find(p => p.path === path && !p.disabled) : null;
                if (rec && rec.content) {
                  try { const parsed = JSON.parse(rec.content); if (parsed && parsed.popupId) ensuredPopupId = String(parsed.popupId); } catch {}
                }
              } catch {}
            }
            // If still unknown, try to find popup by episode playId across all popups
            if (!ensuredPopupId && pid) {
              try {
                const all = await popupsFetchOnce();
                const hit = (all || []).find(p => Array.isArray(p?.episodes) && p.episodes.some(e => String(e?.playId || '') === pid));
                if (hit && hit.id) ensuredPopupId = String(hit.id);
              } catch {}
            }
            if (ensuredPopupId) {
              let popup = byPopup.get(ensuredPopupId);
              if (!popup) {
                const r = await fetch(`/api/popups/${encodeURIComponent(ensuredPopupId)}`, { cache: 'no-store' });
                if (r.ok) popup = await r.json();
                byPopup.set(ensuredPopupId, popup || {});
              }
              if (popup) {
                // If original play page no longer exists, fallback to preview link so item remains usable
                const hasPage = pid ? await pageExists(pid) : false;
                const ep = Array.isArray(popup.episodes) ? popup.episodes.find(e => String(e.playId||'') === pid) : null;
                const img = (popup.listThumb || popup.searchPoster || popup.heroImage || (ep && ep.thumb) || it.img || '');
                const epTitle = (ep && ep.title) || it.epTitle || '';
                const showTitle = popup.title || it.showTitle || '';
                const season = it.season!=null ? it.season : (ep && ep.season!=null ? ep.season : null);
                const index = it.index!=null ? it.index : (ep && ep.index!=null ? ep.index : null);
                const contentType = popup.contentType || it.contentType || '';
                const year = popup.year || it.year || '';
                const genres = Array.isArray(popup.genres) ? popup.genres.slice(0,3) : (Array.isArray(it.genres) ? it.genres.slice(0,3) : []);
                const fallbackPath = contentType === 'film' ? `/filmy?preview=${encodeURIComponent(ensuredPopupId)}` : `/serialy?preview=${encodeURIComponent(ensuredPopupId)}`;
                // Always prefer direct play route when we know playId; fallback preview is only for items without playId
                const nextPath = pid ? `/play/${pid}` : fallbackPath;
                // Persist learned popupId and current image so future loads are fast
                try {
                  if ((!it.popupId || it.img !== img || it.showTitle !== showTitle || it.epTitle !== epTitle) && ensuredPopupId) {
                    await contApi.upsertSeries(user.uid, ensuredPopupId, {
                      playId: pid,
                      path: pid ? `/play/${pid}` : fallbackPath,
                      epTitle,
                      showTitle,
                      img,
                      season,
                      index,
                    });
                  }
                } catch {}
                return { ...it, popupId: ensuredPopupId, img, epTitle, showTitle, season, index, contentType, year, genres, path: nextPath };
              }
            }
            // If we still don't know popup or page, keep the item but link to preview if we can infer
            const hasPage = pid ? await pageExists(pid) : false;
            if (hasPage) return it;
            return null;
          } catch {}
          return it;
        };

        let enriched = base;
        try { const tmp = await Promise.all((base||[]).map(enrichOne)); enriched = tmp.filter(Boolean); } catch {}
        // Deduplicate by popupId (or fallback to playId) and limit to 18
        const dedup = [];
        const seen = new Set();
        for (const it of enriched) {
          if (!it) continue;
          const key = it.popupId ? `p:${it.popupId}` : (it.playId ? `v:${it.playId}` : null);
          if (!key || seen.has(key)) continue;
          seen.add(key);
          dedup.push(it);
          if (dedup.length >= 18) break;
        }
        if (mounted) setItems(dedup);
        if (mounted) setContReady(true);
      } catch {
        if (mounted) setItems([]);
        if (mounted) setContReady(true);
      }
    });
    return () => { mounted = false; unsub && unsub(); };
  }, [user?.uid]);

  // React to popup deletions to refresh list quickly
  useEffect(() => {
    function onDeleted() {
      // trigger reload by changing a dummy state key via user uid change equivalent
      setItems((arr) => arr.filter(() => true));
    }
    window.addEventListener('popup:deleted', onDeleted);
    return () => window.removeEventListener('popup:deleted', onDeleted);
  }, []);

  // When Admin changes popup (title/image/episodes), update Continue items immediately and persist to Firestore
  useEffect(() => {
    if (!user) return;
    const handler = async (ev) => {
      try {
        const detail = ev && ev.detail ? ev.detail : {};
        const popupId = detail && detail.popupId != null ? String(detail.popupId) : null;
        if (!popupId) return;
        let popup = null;
        try {
          const r = await fetch(`/api/popups/${encodeURIComponent(popupId)}`, { cache: 'no-store' });
          if (r.ok) popup = await r.json();
        } catch {}
        const payloadImg = detail && detail.payload && detail.payload.img ? detail.payload.img : '';
        const derivedImg = popup ? (popup.listThumb || popup.searchPoster || popup.heroImage || '') : '';
        const newImg = payloadImg || derivedImg;
        const newShowTitle = popup?.title || '';
        setItems((prev) => {
          const next = (prev || []).map((it) => {
            if (!it) return it;
            const pid = it.popupId != null ? String(it.popupId) : null;
            const sameById = pid && pid === popupId;
            const sameByTitle = !pid && popup && it.showTitle && it.showTitle === popup.title;
            if (sameById || sameByTitle) {
              // persist new thumbnail so Firestore stays in sync
              try {
                contApi.upsertSeries(user.uid, popupId, {
                  playId: it.playId || null,
                  path: it.path || (it.playId ? `/play/${it.playId}` : ''),
                  epTitle: it.epTitle || '',
                  showTitle: newShowTitle || it.showTitle || '',
                  img: newImg || it.img || '',
                  season: it.season ?? null,
                  index: it.index ?? null,
                });
              } catch {}
              // update epTitle if this is a serial and we can find the episode
              let epTitle = it.epTitle || '';
              try {
                if (popup && Array.isArray(popup.episodes)) {
                  const pidStr = (it.playId || '').toString();
                  const ep = popup.episodes.find(e => String(e.playId||'') === pidStr);
                  if (ep && ep.title) epTitle = ep.title;
                }
              } catch {}
              return { ...it, popupId: popupId, img: newImg || it.img, showTitle: (newShowTitle || it.showTitle), epTitle };
            }
            return it;
          });
          return next;
        });
      } catch {}
    };
    window.addEventListener('catalog:changed', handler);
    window.addEventListener('search:changed', handler);
    return () => {
      window.removeEventListener('catalog:changed', handler);
      window.removeEventListener('search:changed', handler);
    };
  }, [user?.uid]);

  return (
    <HomeContainer>
      <Content>
        <ImgSlider />
        {!!user && contReady && !!items.length && (
          <ContinueContainer>
            <Zacatek>
              <h4>Pokračovat ve sledování</h4>
            </Zacatek>
            {items.length < 5 ? (
              <Row>
                {items.map((it) => {
                  const pid = it.playId ? String(it.playId) : '';
                  const href = pid ? `/play/${pid}` : (it.path || '#');
                  const s = it.season!=null ? String(it.season).padStart(2,'0') : null;
                  const e = it.index!=null ? String(it.index).padStart(2,'0') : null;
                  const isFilm = (it.contentType === 'film');
                  const genresTxt = Array.isArray(it.genres) && it.genres.length ? it.genres.slice(0,3).join(', ') : '';
                  const yearTxt = it.year ? String(it.year) : '';
                  const filmSub = [yearTxt, genresTxt].filter(Boolean).join(' • ');
                  const sub = isFilm ? filmSub : (s && e ? `Ř${s}D${e} ${it.epTitle||''}` : (it.epTitle || ''));
                  return (
                    <RowItem key={it.popupId || it.playId}>
                      <Wrap>
                        <button
                          className="remove"
                          title="Odebrat z pokračování"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!user || !it?.popupId) return;
                            setConfirmRemove({ popupId: String(it.popupId), title: it.showTitle || 'Položka' });
                          }}
                        >
                          <span className="icon-outline"><CiCircleMinus /></span>
                          <span className="icon-solid"><FaCircleMinus /></span>
                        </button>
                        <a href={href}>
                          <div className="thumb">
                            <img src={it.img || ''} alt={it.epTitle || it.showTitle || 'Epizoda'} />
                            <div className="play"><svg width="54" height="54" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 5v14l11-7L8 5z" fill="#fff"/></svg></div>
                          </div>
                          <div className="meta">
                            <div className="show">{it.showTitle || 'Seriál'}</div>
                            <div className="sub">{sub}</div>
                          </div>
                        </a>
                      </Wrap>
                    </RowItem>
                  );
                })}
              </Row>
            ) : (
              <Carousel
                dots={false}
                infinite={false}
                speed={250}
                slidesToShow={5}
                slidesToScroll={5}
                variableWidth={false}
                autoplay={false}
                arrows={true}
                responsive={[
                  {
                    breakpoint: 1024,
                    settings: {
                      slidesToShow: 1,
                      slidesToScroll: 1,
                      infinite: false,
                      dots: false,
                      arrows: false,
                    },
                  },
                ]}
              >
                {items.map((it) => {
                  const pid = it.playId ? String(it.playId) : '';
                  const href = pid ? `/play/${pid}` : (it.path || '#');
                  const s = it.season!=null ? String(it.season).padStart(2,'0') : null;
                  const e = it.index!=null ? String(it.index).padStart(2,'0') : null;
                  const isFilm = (it.contentType === 'film');
                  const genresTxt = Array.isArray(it.genres) && it.genres.length ? it.genres.slice(0,3).join(', ') : '';
                  const yearTxt = it.year ? String(it.year) : '';
                  const filmSub = [yearTxt, genresTxt].filter(Boolean).join(' • ');
                  const sub = isFilm ? filmSub : (s && e ? `Ř${s}D${e} ${it.epTitle||''}` : (it.epTitle || ''));
                  return (
                    <Wrap key={it.popupId || it.playId}>
                      <button
                        className="remove"
                        title=""  
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!user || !it?.popupId) return;
                          setConfirmRemove({ popupId: String(it.popupId), title: it.showTitle || 'Položka' });
                        }}
                      >
                        <span className="icon-outline"><CiCircleMinus /></span>
                        <span className="icon-solid"><FaCircleMinus /></span>
                      </button>
                      <a href={href}>
                        <div className="thumb">
                          <img src={it.img || ''} alt={it.epTitle || it.showTitle || 'Epizoda'} />
                          <div className="play"><svg width="54" height="54" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 5v14l11-7L8 5z" fill="#fff"/></svg></div>
                        </div>
                        <div className="meta">
                          <div className="show">{it.showTitle || 'Seriál'}</div>
                          <div className="sub">{sub}</div>
                        </div>
                      </a>
                    </Wrap>
                  );
                })}
              </Carousel>
            )}
          </ContinueContainer>
        )}
      </Content>
      {/* Confirm remove modal */}
      {confirmRemove && (
        <ConfirmOverlay onClick={() => setConfirmRemove(null)}>
          <ConfirmModal onClick={(e) => e.stopPropagation()}>
            <h3>Odebrat z pokračování?</h3>
            <p>Opravdu chceš odebrat „{confirmRemove.title}“ ze seznamu Pokračovat ve sledování?</p>
            <ConfirmButtons>
              <button className="secondary" onClick={() => setConfirmRemove(null)}>Zrušit</button>
              <button
                className="danger"
                onClick={async () => {
                  try {
                    if (user && confirmRemove?.popupId) {
                      await contApi.removeSeries(user.uid, confirmRemove.popupId);
                    }
                  } catch {}
                  setConfirmRemove(null);
                }}
              >Odebrat</button>
            </ConfirmButtons>
          </ConfirmModal>
        </ConfirmOverlay>
      )}
    </HomeContainer>
  );
}

export default Home;

// Styled components to mirror the requested slider/wrap UI
const ContinueContainer = styled.div`
  padding: 0 calc(3.5vw + 5px);
  margin-top: 24px;
`;

const Zacatek = styled.div`
  h4 { color: #fff; margin: 8px 0; font-size: 25px; }
  @media (max-width: 768px) {
    h4 { font-size: 22px; }
  }
`;

const Carousel = styled(Slider)`
  .slick-slide { padding: 10px; }
  .slick-list { overflow: visible; }
  /* Ensure left alignment when fewer slides than visible */
  .slick-track {
    display: flex !important;
    justify-content: flex-start;
    align-items: stretch;
  }
  .slick-slide {
    float: none !important; /* when track is flex, disable float to avoid layout quirks */
    height: auto;
  }
  .slick-arrow {
    opacity: 1;
    transition: opacity .25s ease-in-out;
    top: 50%;
    transform: translateY(-50%);
    z-index: 3;
  }
  .slick-prev { margin-top: -36px; }
  .slick-next { margin-top: -36px; }
`;

const Wrap = styled.div`
  position: relative;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  -webkit-transition: all 0.3s ease-in-out;
  outline: 0px solid transparent;
  transform-origin: center center;
  background: none !important;
 will-change: transform;
  a {
    text-decoration: none;
    color: inherit;
    display: block;
    background: none !important;
  }

  /* small remove button in top-right */
  .remove {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0,0,0,0.25);
    border: none;
    border-radius: 50%;
    color: #fff;
    cursor: pointer;
    z-index: 4;
    transition: background .2s ease, opacity .3s ease;
    opacity: 0; /* hidden by default, show on hover */
  }
  .remove:hover { background: rgba(0,0,0,0.45); }
  .remove .icon-outline, .remove .icon-solid { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }
  .remove .icon-outline { opacity: 1; transition: opacity .3s ease; }
  .remove .icon-solid { opacity: 0; transition: opacity .3s ease; }
  .remove:hover .icon-outline { opacity: 0; }
  .remove:hover .icon-solid { opacity: 1; }

  &:hover .remove { opacity: 1; }

  .thumb {
    position: relative;
    border-radius: 4px;
    overflow: hidden;
    background: linear-gradient(rgb(48, 50, 62), rgb(30, 31, 42));
  }
  /* inner border ring on hover (no layout shift) */
  .thumb::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 3.2px solid #C7C7C7;
    border-radius: 4px;
    pointer-events: none;
    opacity: 0;
    transition: opacity .300s ease;
    z-index: 3;
  }
  .thumb::before {
    content: '';
    display: block;
    padding-top: 56.25%;
  }
  .thumb img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: 1;
    transition: transform .18s ease;
  }
  .thumb .play {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity .15s ease;
    background: linear-gradient(0deg, rgba(0,0,0,0.18), rgba(0,0,0,0.18));
    z-index: 2;
  }

  .meta {
  background-color: transparent;
    padding: 8px 4px 0;
  }
  .meta * {
  background-color: transparent;
    background: none !important;
  }
    
  .meta .show {
  background-color: transparent;
  font-size: 14px;
    color: #B7B8BD;
    font-weight: 800;
    transform-origin: center;
    line-height: 1.2;
    white-space: nowrap; /* vždy jen jeden řádek */
    overflow: hidden;
    text-overflow: ellipsis; /* tři tečky na konci */
    background: none !important;
  }
    background-color: transparent;
    font-size: 12px;
    color: #c8c8c8;
    transform-origin: center;
    margin-top: 2px;
    line-height: 1.6;
    background: none !important;
  }

  &:hover {
    transform: scale(1.035);
    position: relative;
    z-index: 2;
  }

  &:hover .thumb::after { opacity: 1; }
  &:hover .thumb .play { opacity: 1; }
  &:hover .meta .show { color: #fff; }
`;

// Non-slider layout for <5 items, left-aligned tiles sized to 1/5 width baseline
const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -10px;
`;

const RowItem = styled.div`
  padding: 10px;
  width: 20%;
  min-width: 240px;
  @media (max-width: 1024px) {
    width: 100%;
    min-width: 0;
  }
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn .2s ease forwards;
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
`;

const ConfirmModal = styled.div`
  width: min(92vw, 420px);
  background: #1f1f27;
  border: 1px solid #2e2f3a;
  border-radius: 10px;
  padding: 18px 18px 12px;
  color: #fff;
  box-shadow: 0 10px 30px rgba(0,0,0,0.35);
  h3 { margin: 0 0 8px 0; font-size: 18px; }
  p { margin: 0 0 14px 0; color: #c8c8c8; font-size: 14px; }
`;

const ConfirmButtons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  button {
    border-radius: 8px;
    padding: 10px 14px;
    border: none;
    font-weight: 700;
    cursor: pointer;
  }
  .secondary { background: #2b2c36; color: #fff; }
  .secondary:hover { background: #343545; }
  .danger { background: #da3a3a; color: #fff; }
  .danger:hover { background: #c53232; }
`;
